<template>
  <WLPopover
    class="wl-base-select"
    :trigger="PopoverTrigger.MANUAL"
    :container-classes="[
      'shadow-down-lg max-w-full z-50 rounded-2xl',
      containerClasses,
    ]"
    :padding="12"
    :placement="placement"
    has-teleport
    v-model:is-open="isOpen"
  >
    <template #anchor="{ isPopoverVisible }">
      <button
        ref="buttonRef"
        type="button"
        class="wl-select-button font-display text-emphasis box-border flex h-8 flex-nowrap items-center rounded-2xl border border-solid border-transparent py-2 pr-1.5 pl-2 text-start font-normal transition-colors duration-200 outline-none select-none"
        :class="[
          `type-${type}`,
          {
            'focus-visible:outline-accent focus-visible:outline-1 focus-visible:outline-offset-1':
              !isPopoverVisible && !isDisabled,
          },
          isDisabled ? 'is-disabled cursor-not-allowed' : 'cursor-pointer',
          {
            'is-active !border-emphasis !bg-default': isPopoverVisible,
          },
          isFullWidth ? 'w-full' : 'w-max',
        ]"
        :disabled="isDisabled"
        @keydown.esc="isOpen = false"
        @click.stop="handleAnchorClick"
      >
        <WLIcon
          v-if="labelIcon"
          :name="labelIcon"
          :size="IconSize.SMALL"
          :has-hover="false"
          class="mt-0.5 mr-1"
          :class="{ active: isOpen }"
        />
        <span ref="labelRef" class="mr-1 grow truncate text-sm font-medium">
          {{ label }}
        </span>
        <span
          v-if="subLabel"
          ref="subLabelRef"
          class="wl-dropdown-button-sublabel"
          :class="{ active: isOpen }"
        >
          {{ subLabel }}
        </span>
        <WLIcon
          :name="IconName.NAVIGATION_CHEVRON_DOWN"
          :size="IconSize.SMALL"
          :has-hover="false"
          :class="{ active: isOpen }"
          class="text-muted"
        />
      </button>
    </template>
    <template #content>
      <slot />
    </template>
  </WLPopover>
</template>

<script lang="ts" setup>
import { ref, toRefs, watch } from "vue";
import { useWebHaptics } from "../../composables/use-web-haptics";
import { type WLBaseSelectProps } from "./types";
import { SelectType } from "./constants";
import { WLPopover, PopoverTrigger } from "../popover";
import { IconName, IconSize, WLIcon } from "../icon";

const props = withDefaults(defineProps<WLBaseSelectProps>(), {
  type: SelectType.DEFAULT,
  isDisabled: false,
  isFullWidth: false,
  containerClasses: "",
  placement: "bottom-start",
});

const {
  label,
  labelIcon,
  subLabel,
  type,
  isDisabled,
  isFullWidth,
  containerClasses,
  placement,
} = toRefs(props);

const isOpen = defineModel<boolean>("isOpen", { default: false });

const buttonRef = ref<HTMLButtonElement | null>(null);
const { triggerHaptic } = useWebHaptics();

const handleAnchorClick = (): void => {
  if (isDisabled.value) {
    return;
  }

  triggerHaptic("soft");
  isOpen.value = !isOpen.value;
};

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    buttonRef?.value?.focus();
  }
});

defineExpose({ buttonRef });
</script>

<style>
@reference "@theme";

.wl-select-button {
  &.type-default {
    @apply bg-muted hover:border-emphasis;
  }

  &.type-elevated {
    @apply bg-default shadow-down-sm transition-shadow duration-200;
  }

  &.is-disabled {
    @apply cursor-not-allowed border-transparent opacity-50;
  }
}
</style>
